import React from 'react';
import {
  extractValue,
  extractSource,
  extractSourceUser,
  extractSourceAt,
  extractConfidence,
  SOURCE_LABELS,
} from '../utils/metadataUtils';
import { formatDate, formatDateTime, formatNumber } from '../utils/dateUtils';

const formatMetadataValue = (value, tipo) => {
  if (value === null || value === undefined || value === '') return null;
  switch ((tipo || '').toUpperCase()) {
    case 'DATE': {
      const formatted = formatDate(value, { placeholder: '' });
      return formatted || value;
    }
    case 'INTEGER':
    case 'LONG':
      return typeof value === 'number' ? formatNumber(value) : value;
    case 'BOOLEAN':
      return value ? 'Sí' : 'No';
    default:
      return value;
  }
};

const buildSourceTooltip = (source, entry) => {
  if (!source) return '';
  const parts = [`Origen: ${SOURCE_LABELS[source] || source}`];
  const user = extractSourceUser(entry);
  const at = extractSourceAt(entry);
  const confidence = extractConfidence(entry);
  if (user) parts.push(`por ${user}`);
  if (at) {
    const formattedAt = formatDateTime(at, { placeholder: '' });
    if (formattedAt) parts.push(`el ${formattedAt}`);
  }
  if (confidence !== null && confidence !== undefined) {
    parts.push(`confianza ${(confidence * 100).toFixed(0)}%`);
  }
  return parts.join(' · ');
};

const MetadataDisplay = ({ metadata, metadataSpec }) => {
  const specFields = Array.isArray(metadataSpec) ? metadataSpec : [];
  const metadataMap = metadata && typeof metadata === 'object' ? metadata : {};

  // Reconciliar spec + metadata: primero todos los campos del spec (en orden),
  // luego cualquier clave presente en metadata que no esté en el spec (fallback).
  const rows = [];
  const seen = new Set();

  specFields.forEach(campo => {
    const key = campo.name || campo.nombre;
    if (!key) return;
    seen.add(key.toLowerCase().trim());
    const entry = Object.entries(metadataMap).find(
      ([k]) => k.toLowerCase().trim() === key.toLowerCase().trim()
    );
    rows.push({
      key,
      entry: entry ? entry[1] : undefined,
      spec: campo,
    });
  });

  Object.entries(metadataMap).forEach(([key, entry]) => {
    if (seen.has(key.toLowerCase().trim())) return;
    rows.push({ key, entry, spec: null });
  });

  if (rows.length === 0) {
    return (
      <div style={styles.panel}>
        <div style={styles.emptyState}>
          No hay campos de metadata definidos para esta serie documental.
        </div>
      </div>
    );
  }

  return (
    <div style={styles.panel}>
      <div style={styles.grid}>
        {rows.map(({ key, entry, spec }) => {
          const rawValue = extractValue(entry);
          const source = extractSource(entry);
          const label = spec?.label?.trim() || spec?.name || spec?.nombre || key;
          const tipo = spec?.type || spec?.tipo;
          const required = !!(spec?.required);
          const description = spec?.description || spec?.descripcion;
          const formatted = formatMetadataValue(rawValue, tipo);
          const tooltip = buildSourceTooltip(source, entry);
          const sourceLabel = source ? (SOURCE_LABELS[source] || source) : null;

          return (
            <div key={key} style={styles.field}>
              <div style={styles.fieldHeader}>
                <span style={styles.label}>
                  {label}
                  {required && <span style={styles.requiredMark} aria-label="obligatorio">*</span>}
                </span>
                {sourceLabel && (
                  <span style={styles.sourceSideText} title={tooltip}>
                    · {sourceLabel}
                  </span>
                )}
              </div>
              <div
                style={{
                  ...styles.value,
                  ...(formatted === null ? styles.valueEmpty : {}),
                }}
              >
                {formatted === null ? '—' : formatted}
              </div>
              {description && <div style={styles.description}>{description}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const styles = {
  panel: {
    padding: '20px 24px',
    backgroundColor: '#FFFFFF',
    borderTop: '1px solid #E5E7EB',
  },
  emptyState: {
    textAlign: 'center',
    color: '#6B7280',
    fontSize: '13px',
    fontStyle: 'italic',
    padding: '32px 16px',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    columnGap: '32px',
    rowGap: '18px',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    minWidth: 0,
  },
  fieldHeader: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '6px',
    flexWrap: 'wrap',
  },
  label: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#374151',
    letterSpacing: '0.02em',
  },
  requiredMark: {
    color: '#D97706',
    marginLeft: '2px',
    fontWeight: 600,
  },
  sourceSideText: {
    fontSize: '11px',
    fontWeight: 400,
    color: '#9CA3AF',
    cursor: 'help',
  },
  value: {
    fontSize: '14px',
    color: '#111827',
    fontWeight: 500,
    padding: '6px 0 4px',
    borderBottom: '1px solid #F3F4F6',
    wordBreak: 'break-word',
    minHeight: '22px',
  },
  valueEmpty: {
    color: '#9CA3AF',
    fontWeight: 400,
    fontStyle: 'italic',
  },
  description: {
    fontSize: '11px',
    color: '#6B7280',
    fontStyle: 'italic',
    lineHeight: 1.35,
  },
};

export default MetadataDisplay;
